import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { X, Lock, AlertTriangle } from 'lucide-react';
import { useSubscription } from '../hooks/useSubscription.js';
import { FEATURE_LABELS } from '../config/tierLimits.js';
import { useT, useI18nStore } from '../store/i18nStore.js';
import { useLp } from '../hooks/useLp.js';

export default function UpgradeModal() {
  const t = useT();
  const lp = useLp();
  const lang = useI18nStore((s) => s.lang);
  const [open, setOpen] = useState(false);
  const [feature, setFeature] = useState(null);
  const { isExpired, isInactive } = useSubscription();
  const navigate = useNavigate();

  useEffect(() => {
    const onShow = (e) => {
      setFeature(e.detail?.feature || null);
      setOpen(true);
    };
    window.addEventListener('show-upgrade-modal', onShow);
    return () => window.removeEventListener('show-upgrade-modal', onShow);
  }, []);

  if (!open) return null;

  const subscriptionIssue = isExpired || isInactive;
  // FEATURE_LABELS values are in Indonesian; in English mode, fall back to the
  // generic "Premium Feature" label since we don't translate the feature names.
  const featureLabel = (lang === 'ID' && FEATURE_LABELS[feature]) || t('upgrade.featureDefault');

  return (
    <div
      className="fixed inset-0 z-[100] bg-ink-900/60 backdrop-blur-sm flex items-center justify-center p-4"
      onClick={() => setOpen(false)}
    >
      <div
        className="bg-cream-50 rounded-2xl shadow-xl max-w-md w-full p-8 relative"
        onClick={(e) => e.stopPropagation()}
      >
        <button
          onClick={() => setOpen(false)}
          className="absolute top-4 right-4 text-ink-400 hover:text-ink-700 transition"
        >
          <X size={20} strokeWidth={1.6} />
        </button>

        <div className="flex justify-center mb-4">
          <div className="w-14 h-14 rounded-full bg-brand-50 border border-brand-200 flex items-center justify-center text-brand-700">
            {subscriptionIssue ? <AlertTriangle size={22} strokeWidth={1.6} /> : <Lock size={22} strokeWidth={1.6} />}
          </div>
        </div>

        {subscriptionIssue ? (
          <>
            <h3 className="font-serif text-[22px] text-ink-800 text-center mb-2">
              {t('upgrade.subscriptionInactive')}
            </h3>
            <p className="text-[13px] text-ink-600 text-center leading-relaxed mb-6">
              {t('upgrade.subscriptionInactiveDesc')}
            </p>
          </>
        ) : (
          <>
            <h3 className="font-serif text-[22px] text-ink-800 text-center mb-2">{featureLabel}</h3>
            <p className="text-[13px] text-ink-600 text-center leading-relaxed mb-6">
              {t('upgrade.featureDesc')}
            </p>
          </>
        )}

        <div className="flex flex-col gap-2">
          <button
            onClick={() => {
              navigate(lp('/pricing'));
              setOpen(false);
            }}
            className="w-full py-3 rounded-full bg-brand-600 text-cream-50 text-[12px] tracking-widest uppercase hover:bg-brand-700 transition"
          >
            {t('upgrade.viewPlans')}
          </button>
          <button
            onClick={() => setOpen(false)}
            className="w-full py-2.5 text-[12px] tracking-wider text-ink-500 hover:text-ink-700 transition"
          >
            {t('upgrade.later')}
          </button>
        </div>
      </div>
    </div>
  );
}
